import type { JourneyCell } from './journeys'

/**
 * Palette and type scale from the approved Care Journey Board design.
 *
 * The organising idea: a settled step recedes to grey so the only colour on
 * screen belongs to something that needs a person. That is what makes the board
 * scannable, so `quiet` is applied to every completed cell by default.
 */
export const JOURNEY_COLOR = {
  done: '#1a7f5a',
  doneQuiet: '#5c6675',
  wait: '#2f6bd8',
  late: '#b8453b',
  warn: '#9c6b12',
  na: '#727d8c',
  ink: '#1b2430',
  muted: '#6f7a8a',
  subtle: '#8c95a3',
  hairline: '#f1f4f7',
  border: '#e2e6ec',
  surfaceAlt: '#fafbfc',
  ringIdle: '#dfe4ea',
} as const

/** Mono face for dates, orgs and stage labels — the design's texture cue. */
export const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
export const SANS = "'IBM Plex Sans', system-ui, -apple-system, sans-serif"

export type StageTone = 'done' | 'wait' | 'late' | 'na' | 'closed'

/** Map a cell's server-side state onto the design's four tones. */
export function toneOf(cell: JourneyCell): StageTone {
  switch (cell.state) {
    case 'completed':
      return 'done'
    case 'overdue':
      return 'late'
    case 'in-progress':
    case 'future':
      return 'wait'
    case 'declined':
      return 'closed'
    default:
      return 'na'
  }
}

export type NodeStyle = {
  fill: string
  ring: string
  textColor: string
  weight: number
  lineColor: string
  lineStyle: 'solid' | 'dashed'
}

/**
 * Pipeline node styling. A completed node is hollow and grey when `quiet` —
 * present, but not competing with the exception you are meant to notice.
 */
export function nodeStyle(tone: StageTone, quiet: boolean): NodeStyle {
  switch (tone) {
    case 'done':
      return {
        fill: quiet ? '#ffffff' : JOURNEY_COLOR.done,
        ring: JOURNEY_COLOR.done,
        textColor: quiet ? JOURNEY_COLOR.doneQuiet : JOURNEY_COLOR.done,
        weight: quiet ? 450 : 500,
        lineColor: JOURNEY_COLOR.done,
        lineStyle: 'solid',
      }
    case 'wait':
      return {
        fill: '#ffffff',
        ring: JOURNEY_COLOR.wait,
        textColor: JOURNEY_COLOR.wait,
        weight: 500,
        lineColor: '#c9d6ee',
        lineStyle: 'solid',
      }
    // Settled, but not a success: the practitioner declined the patient email,
    // so nothing is still coming. FILLED like a finished stage — a hollow node
    // reads as "in flight" — and blue rather than green, because the patient
    // was never told. The track continues solid past it.
    case 'closed':
      return {
        fill: JOURNEY_COLOR.wait,
        ring: JOURNEY_COLOR.wait,
        textColor: JOURNEY_COLOR.wait,
        weight: 500,
        lineColor: JOURNEY_COLOR.wait,
        lineStyle: 'solid',
      }
    case 'late':
      return {
        fill: JOURNEY_COLOR.late,
        ring: JOURNEY_COLOR.late,
        textColor: JOURNEY_COLOR.late,
        weight: 600,
        lineColor: '#f0cdc9',
        lineStyle: 'solid',
      }
    default:
      return {
        fill: '#ffffff',
        ring: JOURNEY_COLOR.ringIdle,
        textColor: JOURNEY_COLOR.na,
        weight: 400,
        lineColor: '#e4e8ee',
        lineStyle: 'dashed',
      }
  }
}

/**
 * Urgency chip + left bar. Driven ONLY by a follow-up task's due date, because
 * that is the only thing in a journey with a deadline attached (orders and
 * results have none — see `overdueDays` in the contract).
 */
export function severityOf(overdueDays: number | null): {
  bar: string
  bg: string
  color: string
  label: string
} {
  if (overdueDays !== null && overdueDays > 7) {
    return {
      bar: JOURNEY_COLOR.late,
      bg: '#fdf0ef',
      color: JOURNEY_COLOR.late,
      label: `${overdueDays} days overdue`,
    }
  }
  if (overdueDays !== null && overdueDays > 0) {
    return {
      bar: '#e0a23c',
      bg: '#fdf6e9',
      color: JOURNEY_COLOR.warn,
      label: `${overdueDays} ${overdueDays === 1 ? 'day' : 'days'} overdue`,
    }
  }
  return { bar: JOURNEY_COLOR.ringIdle, bg: '#eef4ef', color: '#3f7f63', label: 'On track' }
}

export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export const JOURNEY_LEGEND = [
  { color: JOURNEY_COLOR.done, label: 'Complete' },
  { color: JOURNEY_COLOR.wait, label: 'In progress' },
  { color: JOURNEY_COLOR.late, label: 'Overdue — needs attention' },
  { color: JOURNEY_COLOR.ringIdle, label: 'Not required' },
] as const
